import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { checkApiHealth, clearApiHealthCache, isOfflineTolerantPath } from '../../utils/apiHealth';
import OfflinePage from './OfflinePage';

interface ConnectionGuardProps {
  children: React.ReactNode;
}

/**
 * Shows OfflinePage when offline or when API is unreachable on API-dependent routes.
 * Uses GET /api/health (not site-info) with a shared cache to avoid duplicate 502 noise.
 */
export default function ConnectionGuard({ children }: ConnectionGuardProps) {
  const location = useLocation();
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [apiOk, setApiOk] = useState<boolean | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);

  const tolerant = isOfflineTolerantPath(location.pathname);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      clearApiHealthCache();
      setApiOk(null);
    };
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const runCheck = useCallback(async (force = false) => {
    const ok = await checkApiHealth(force);
    setApiOk(ok);
  }, []);

  useEffect(() => {
    if (!isOnline) {
      setApiOk(false);
      return;
    }
    void runCheck();
  }, [isOnline, runCheck]);

  const handleRetry = useCallback(async () => {
    setIsRetrying(true);
    if (!navigator.onLine) {
      setApiOk(false);
      setIsRetrying(false);
      return;
    }
    clearApiHealthCache();
    await runCheck(true);
    setIsRetrying(false);
  }, [runCheck]);

  if (!isOnline || (apiOk === false && !tolerant)) {
    return (
      <OfflinePage
        onRefresh={handleRetry}
        isError={apiOk === false}
        isLoading={isRetrying}
      />
    );
  }

  return <>{children}</>;
}
